import React from 'react';
import { 
  Flag, 
  Presentation, 
  Image, 
  Megaphone, 
  RectangleHorizontal,
  Tent,
  Table2,
  PanelTop,
  Square,
  Layers,
  LayoutGrid,
  Car,
  Frame,
  ImagePlus,
  Footprints,
} from 'lucide-react';
import { Badge } from '../ui/badge';

// Category icons mapped by category code
const CATEGORY_ICONS = {
  'BAN': RectangleHorizontal,  // Banners
  'FLG': Flag,                 // Flags
  'BNS': PanelTop,             // Banner Stands
  'RGD': Square,               // Rigid Signs
  'ADH': Layers,               // Adhesive Products
  'SEG': PanelTop,             // SEG Displays
  'TDP': Presentation,         // Trade Show
  'TNT': Tent,                 // Tents
  'WAL': Image,                // Wall Graphics
  'WIN': Square,               // Window Graphics
  'FLR': Footprints,           // Floor Graphics
  'VEH': Car,                  // Vehicle Graphics
  'CNV': Frame,                // Canvas Prints
  'ART': ImagePlus,            // Wall Art
  'PHO': Image,                // Photo Prints
  'ACC': LayoutGrid,           // Accessories
  'default': LayoutGrid,
};

// Category display names
const CATEGORY_NAMES = {
  'BAN': 'Banners',
  'FLG': 'Flags',
  'BNS': 'Banner Stands',
  'RGD': 'Rigid Signs',
  'ADH': 'Adhesive Products',
  'SEG': 'SEG Displays',
  'TDP': 'Trade Show',
  'TNT': 'Tents',
  'WAL': 'Wall Graphics',
  'WIN': 'Window Graphics',
  'FLR': 'Floor Graphics',
  'VEH': 'Vehicle Graphics',
  'CNV': 'Canvas Prints',
  'ART': 'Wall Art',
  'PHO': 'Photo Prints',
  'ACC': 'Accessories',
};

// Group categories by type for better organization
const CATEGORY_GROUPS = {
  'Popular': ['BAN', 'FLG', 'BNS', 'RGD'],
  'Trade Show': ['TDP', 'SEG', 'TNT'],
  'Graphics': ['ADH', 'WAL', 'WIN', 'FLR', 'VEH'],
  'Prints & Art': ['CNV', 'ART', 'PHO'],
  'Other': ['ACC'],
};

const CategorySidebar = ({
  categories = [],
  selectedCategories = [],
  onCategoryClick,
  onAllProductsClick,
  totalProductCount = 0,
  loading = false,
}) => {
  // Get category info - normalize count field (API returns product_count)
  const getCategoryInfo = (code) => {
    const cat = categories.find(c => c.code === code || c.id === code);
    return {
      id: cat?.id || code,
      name: cat?.name || CATEGORY_NAMES[code] || code,
      count: cat?.product_count || cat?.count || 0,
      code: cat?.code || code,
    };
  };

  // Build grouped categories with counts
  const buildGroupedCategories = () => {
    const grouped = {};
    
    for (const [groupName, codes] of Object.entries(CATEGORY_GROUPS)) {
      const groupCategories = codes
        .map(code => getCategoryInfo(code))
        .filter(cat => cat.count > 0);
      
      if (groupCategories.length > 0) {
        grouped[groupName] = groupCategories;
      }
    }
    
    // Add ungrouped categories (those not in predefined groups)
    const groupedCodes = Object.values(CATEGORY_GROUPS).flat();
    const ungrouped = categories
      .filter(cat => cat.code && !groupedCodes.includes(cat.code) && (cat.product_count || cat.count || 0) > 0)
      .map(cat => ({
        id: cat.id,
        name: cat.name,
        count: cat.product_count || cat.count || 0,
        code: cat.code,
      }));
    
    if (ungrouped.length > 0) {
      grouped['Other'] = ungrouped;
    }
    
    return grouped;
  };

  const groupedCategories = buildGroupedCategories();
  const isSelected = (categoryId) => selectedCategories.includes(categoryId);
  const isAllSelected = selectedCategories.length === 0;

  if (loading) {
    return (
      <div className="bg-ivory-100 rounded-xl border border-ivory-300/50 shadow-luxury p-4 sticky top-4">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-ivory-200 rounded w-2/3"></div>
          <div className="h-4 bg-ivory-200 rounded w-full"></div>
          <div className="h-4 bg-ivory-200 rounded w-3/4"></div>
          <div className="h-4 bg-ivory-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ivory-100 rounded-xl border border-ivory-300/50 shadow-luxury p-4 sticky top-4" data-testid="category-sidebar">
      {/* Header */}
      <h2 className="text-lg font-heading font-semibold text-ivory-800 mb-4 pb-3 border-b border-ivory-200 flex items-center justify-between">
        <span>Categories</span>
        <Badge variant="outline" className="text-[10px] font-body text-ivory-500">
          {categories.length} types
        </Badge>
      </h2>
      
      {/* All Products */}
      <button
        onClick={onAllProductsClick}
        className={`w-full text-left px-3 py-2.5 rounded-lg font-body text-sm transition-all duration-200 flex items-center justify-between mb-3 ${
          isAllSelected
            ? 'bg-gold-500 text-white shadow-md'
            : 'text-ivory-600 hover:bg-ivory-200/70 hover:text-ivory-800'
        }`}
        data-testid="category-all"
      >
        <span className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            isAllSelected ? 'bg-white/20' : 'bg-ivory-200'
          }`}>
            <LayoutGrid className={`w-4 h-4 ${isAllSelected ? 'text-white' : 'text-gold-600'}`} />
          </div>
          <span className="font-medium">All Products</span>
        </span>
        <Badge 
          variant={isAllSelected ? "secondary" : "outline"} 
          className={`text-[10px] ${isAllSelected ? 'bg-white/20 text-white border-0' : 'text-ivory-500'}`}
        >
          {totalProductCount}
        </Badge>
      </button>

      {/* Grouped Categories */}
      <nav className="space-y-4">
        {Object.entries(groupedCategories).map(([groupName, groupCats]) => (
          <div key={groupName}>
            {/* Group Header */}
            <h3 className="text-[10px] uppercase tracking-wider text-ivory-400 font-body font-semibold mb-2 px-1">
              {groupName}
            </h3>
            
            {/* Category Items */}
            <div className="space-y-1">
              {groupCats.map((category) => {
                const IconComponent = CATEGORY_ICONS[category.code] || CATEGORY_ICONS['default'];
                const selected = isSelected(category.id);
                
                return (
                  <button
                    key={category.id}
                    onClick={() => onCategoryClick(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg font-body text-sm transition-all duration-200 flex items-center justify-between group ${
                      selected
                        ? 'bg-gold-500/10 text-gold-700 border border-gold-500/30'
                        : 'text-ivory-600 hover:bg-ivory-200/50 hover:text-ivory-800 border border-transparent'
                    }`}
                    data-testid={`category-sidebar-${category.id}`}
                  >
                    <span className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                        selected ? 'bg-gold-500/20' : 'bg-ivory-200/70 group-hover:bg-ivory-300/70'
                      }`}>
                        <IconComponent className={`w-3.5 h-3.5 ${
                          selected ? 'text-gold-600' : 'text-ivory-500 group-hover:text-ivory-700'
                        }`} />
                      </div>
                      <span className={selected ? 'font-medium' : ''}>{category.name}</span>
                    </span>
                    <Badge 
                      variant="outline" 
                      className={`text-[10px] ${selected ? 'text-gold-600 border-gold-300' : 'text-ivory-400'}`}
                    >
                      {category.count}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      
      {/* Footer Help Text */}
      <div className="mt-6 pt-4 border-t border-ivory-200">
        <p className="text-[11px] text-ivory-400 font-body text-center">
          Can&apos;t find what you need?{' '}
          <a href="/contact" className="text-gold-600 hover:underline">Contact us</a>
        </p>
      </div>
    </div>
  );
};

export default CategorySidebar;
